import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CURRENCY_SYMBOLS } from '../../utils/currencyUtils';

const FormContainer = styled.div`
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 14px;
  padding: 20px;
  box-shadow: 
    4px 4px 10px rgba(0, 0, 0, 0.05),
    -4px -4px 10px rgba(255, 255, 255, 0.8);
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #4a6fa1;
  margin-bottom: 8px;
`;

const CustomSelect = styled.div`
  position: relative;
  width: 100%;
`;

const SelectedOption = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #f0f3f9;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.05),
              inset -2px -2px 5px rgba(255, 255, 255, 0.7);
  
  &:after {
    content: '';
    margin-left: auto;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #4a6fa1;
  }
`;

const DropdownOptions = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin-top: 8px;
  background: #f7f9fc;
  border-radius: 10px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const Option = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #e6ebf5;
  }
`;

const InputGroup = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.05),
              inset -2px -2px 5px rgba(255, 255, 255, 0.7);
`;

const Input = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: #f0f3f9;
  color: #333;
  font-size: 16px;
  
  &:focus {
    outline: none;
    background: #e6ebf5;
  }
`;

const CurrencySelectWrapper = styled.div`
  position: relative;
  width: 60px;
  background: #e6ebf5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CurrencySelect = styled.select`
  width: 100%;
  height: 100%;
  padding: 0 16px 0 10px;
  border: none;
  background: transparent;
  color: #4a6fa1;
  font-weight: 500;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &:focus {
    outline: none;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid #4a6fa1;
`;

const SaveButton = styled.button`
  display: block;
  width: 100%;
  padding: 14px;
  background: linear-gradient(145deg, #4a6fa1, #5d82b3);
  color: white;
  font-size: 16px;
  font-weight: 500;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1),
              -2px -2px 5px rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: linear-gradient(145deg, #4263a1, #5275b3);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1),
                -1px -1px 3px rgba(255, 255, 255, 0.1);
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #8c96a5;
`;

const BudgetForm = ({ categories, onSave, defaultCurrency = 'UAH' }) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [limit, setLimit] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency);
  const dropdownRef = useRef(null);

  // Закрытие выпадающего списка при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Обновляем валюту по умолчанию при её изменении
  useEffect(() => {
    setCurrency(defaultCurrency);
  }, [defaultCurrency]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsDropdownOpen(false);
  };

  const handleSubmit = async () => {
    if (!selectedCategory || !limit || isNaN(parseFloat(limit)) || parseFloat(limit) <= 0) {
      alert(t('fill_fields'));
      return;
    }

    await onSave(selectedCategory, parseFloat(limit), currency);
    
    // Сбрасываем форму
    setSelectedCategory('');
    setLimit('');
  };

  return (
    <FormContainer>
      <FormGroup>
        <Label>{t('category')}</Label>
        <CustomSelect ref={dropdownRef}>
          <SelectedOption onClick={toggleDropdown}>
            {selectedCategory || t('select_category')}
          </SelectedOption>
          <DropdownOptions $isOpen={isDropdownOpen}>
            {categories.length > 0 ? (
              categories.map((category, index) => (
                <Option 
                  key={`${category.name}-${index}`}
                  onClick={() => handleCategorySelect(category.name)}
                >
                  {category.name}
                </Option>
              ))
            ) : (
              <EmptyMessage>{t('no_categories')}</EmptyMessage>
            )}
          </DropdownOptions>
        </CustomSelect>
      </FormGroup>

      <FormGroup>
        <Label>{t('limit')}</Label>
        <InputGroup>
          <Input
            type="number"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder={t('enter_amount')}
            min="0"
            step="0.01"
          />
          <CurrencySelectWrapper>
  <CurrencySelect
    value={currency}
    onChange={(e) => setCurrency(e.target.value)}
  >
    {Object.entries(CURRENCY_SYMBOLS).map(([code, symbol]) => (
      <option key={code} value={code}>
        {symbol}
      </option>
    ))}
  </CurrencySelect>
  <SelectArrow />
</CurrencySelectWrapper>
        </InputGroup>
      </FormGroup>

      <SaveButton onClick={handleSubmit}>
        {t('save')}
      </SaveButton>
    </FormContainer>
  );
};

export default BudgetForm;